import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { SocketKey } from '@/Config/api/socketKey';
import { IndexPath } from '@/Config/app/router/index.path';
import {
  PaginatedResponse,
  RunDetailDto,
  RunListDto,
  RunResultDto,
  RunResultStatus,
} from '@/Domain/api/Simulation/dto/Simulation.dto';
import { MessageFeedbackButton } from '@/Domain/components/Inbox/Message/MessageFeedbackButton';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/Domain/components/Shared/Breadcrumb/Breadcrumb';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useSocket } from '@/Domain/components/SocketProvider';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { StatusBadge } from '@/UI/Simulation/components/StatusBadge';
import {
  getScoreColor,
  resultErrorText,
  toResultErrorCode,
} from '@/UI/Simulation/components/simulationUtils';

type Props = {
  runId: number;
  datasetName: string;
  onBackToDataset: () => void;
  onBackToList: () => void;
};

// Weight contribution of a single result toward the run's progress, by status.
// Each message has two stages: simulation (heavier) and evaluation (lighter).
// pending/running both map to 0 so the bar starts at 0% — the 33% we used to
// see was because all 4 results flipped to RUNNING in parallel before any
// actual work had completed.
const RESULT_PROGRESS_WEIGHT: Record<RunResultStatus, number> = {
  pending: 0,
  running: 0,
  evaluating: 0.7,
  completed: 1,
  failed: 1,
};

export const RunDetailView = ({ runId, datasetName, onBackToDataset, onBackToList }: Props) => {
  const { t } = useTranslation('simulation_page');
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { scenario = '' } = useParams<{ scenario: string }>();

  const { data: run, isLoading } = useQuery<RunDetailDto>({
    queryKey: [QueryKey.simulation.runDetail, runId.toString()],
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'running' || status === 'pending' ? 3000 : false;
    },
  });

  useEffect(() => {
    if (!run) return;
    capture(PostHogEventEnum.SimulationRunOpened, {
      run_id: runId,
      status: run.status,
      total_messages: run.total_messages,
      avg_eval_score: run.avg_eval_score,
    });
  }, [run?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastTerminalStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!run) return;
    const terminal = ['completed', 'failed', 'cancelled'];
    if (!terminal.includes(run.status)) return;
    if (lastTerminalStatusRef.current === run.status) return;
    lastTerminalStatusRef.current = run.status;
    capture(PostHogEventEnum.SimulationRunCompleted, {
      run_id: runId,
      status: run.status,
      total_messages: run.total_messages,
      completed_messages: run.completed_messages,
      failed_messages: run.failed_messages,
      avg_eval_score: run.avg_eval_score,
    });
  }, [run?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancelMutation = useMutation<RunListDto, unknown, number>({
    mutationKey: [QueryKey.simulation.cancelRun],
  });

  const { socket } = useSocket();

  // Listen for aggregate progress
  useEffect(() => {
    const progressHandler = (data: Record<string, unknown>) => {
      if (data.run_id !== runId) return;
      queryClient.invalidateQueries({
        queryKey: [QueryKey.simulation.runDetail, runId.toString()],
      });
      // The Simulation dataset list shows each dataset's latest run
      // avg score — keep it fresh as the run progresses and finishes.
      queryClient.invalidateQueries({ queryKey: [QueryKey.simulation.datasets] });
      // Patch every cached runs list so the dataset detail's Runs tab shows
      // the live status the moment the user navigates back — without it the
      // cached "generating" row is shown until the 5s poll catches up.
      queryClient.setQueriesData<PaginatedResponse<RunListDto>>(
        { queryKey: [QueryKey.simulation.runs], exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((r) =>
              r.id === runId
                ? {
                    ...r,
                    status: (data.status as RunListDto['status']) ?? r.status,
                    completed_messages:
                      typeof data.completed === 'number' ? data.completed : r.completed_messages,
                    failed_messages:
                      typeof data.failed === 'number' ? data.failed : r.failed_messages,
                  }
                : r,
            ),
          };
        },
      );
    };
    socket.on(SocketKey.simulation.runProgress, progressHandler);
    return () => {
      socket.off(SocketKey.simulation.runProgress, progressHandler);
    };
  }, [socket, queryClient, runId]);

  // Compute stats from results (auto-updates on every WS patch)
  const stats = useMemo(() => {
    if (!run)
      return {
        completed: 0,
        aiScore: null as number | null,
        yourScore: null as number | null,
        progress: 0,
      };
    const results = run.results;
    const completed = results.filter(
      (r) => r.status === 'completed' || r.status === 'failed',
    ).length;

    const scores = results.map((r) => r.eval_score).filter((s): s is number => s != null);
    const aiScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;

    const positive = results.filter((r) => r.feedback?.value === 'positive').length;
    const negative = results.filter((r) => r.feedback?.value === 'negative').length;
    const rated = positive + negative;
    const yourScore = rated > 0 ? Math.round((positive / rated) * 100) : null;

    const totalWeight = run.total_messages;
    const completedWeight = results.reduce(
      (acc, r) => acc + (RESULT_PROGRESS_WEIGHT[r.status] ?? 0),
      0,
    );
    const progress =
      run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled'
        ? 100
        : totalWeight > 0
          ? Math.min(99, Math.round((completedWeight / totalWeight) * 100))
          : 0;

    return { completed, aiScore, yourScore, progress };
  }, [run]);

  if (isLoading || !run) {
    return (
      <div className="space-y-6" data-testid="run-detail-loading">
        <Skeleton className="h-4 w-48" />
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-6 w-24" />
        </div>
        <Skeleton className="h-2 w-full" />
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    );
  }

  const hasQuotaError = run.results.some((r) => r.error_message === 'message_quota_exceeded');

  const runLabel = run.label || `Run #${run.ord_number}`;

  return (
    <div className="space-y-6" data-testid="run-detail-view">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink data-testid="run-detail-breadcrumb-root" onClick={onBackToList}>
              {t('datasets_breadcrumb')}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink data-testid="run-detail-breadcrumb-dataset" onClick={onBackToDataset}>
              {datasetName}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage data-testid="run-detail-breadcrumb-current">{runLabel}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 data-testid="run-detail-label" className="text-lg font-semibold">
            {runLabel}
          </h1>
          {run.status !== 'completed' && <StatusBadge status={run.status} />}
        </div>
        {(run.status === 'pending' || run.status === 'running') && (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              cancelMutation.mutate(runId, {
                onSuccess: () => {
                  toast.success(t('toast_run_cancelled'));
                  queryClient.invalidateQueries({
                    queryKey: [QueryKey.simulation.runDetail, runId.toString()],
                  });
                },
              })
            }
          >
            {t('cancel_run')}
          </Button>
        )}
      </div>

      {/* Summary stats */}
      <div className="flex gap-4 text-sm">
        <div>
          <span className="text-muted-foreground">{t('completed_label')}</span>{' '}
          <span className="font-medium">{stats.completed}</span>
        </div>
        {run.results.some((r) => r.status === 'failed') && (
          <div>
            <span className="text-muted-foreground">{t('failed_label')}</span>{' '}
            <span className="font-medium text-destructive">
              {run.results.filter((r) => r.status === 'failed').length}
            </span>
          </div>
        )}
        {stats.aiScore != null && (
          <div>
            <span className="text-muted-foreground">{t('avg_score_label')}</span>{' '}
            <span className={`font-semibold ${getScoreColor(stats.aiScore)}`}>
              {stats.aiScore.toFixed(1)}/5
            </span>
          </div>
        )}
        {stats.yourScore != null && (
          <div>
            <span className="text-muted-foreground">{t('your_score_label')}</span>{' '}
            <span className="font-semibold">{stats.yourScore}%</span>
          </div>
        )}
      </div>

      {/* Quota exhaustion banner */}
      {hasQuotaError && (
        <div
          data-testid="quota-banner"
          className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive-muted px-4 py-3 text-sm text-destructive"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-medium">{t('quota_exhausted_banner')}</p>
            <button
              className="mt-1 text-sm font-medium text-destructive underline"
              onClick={() => navigate(`/i/${scenario}/${IndexPath.subscription}`)}
            >
              {t('upgrade_plan')}
            </button>
          </div>
        </div>
      )}

      {/* Results table */}
      <div className="rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="w-12 px-3 py-2 text-left font-medium">#</th>
              <th className="px-3 py-2 text-left font-medium">{t('column_message')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('column_ai_response')}</th>
              <th className="w-24 whitespace-nowrap px-3 py-2 text-left font-medium">
                {t('column_ai_score')}
              </th>
              <th className="px-3 py-2 text-left font-medium">{t('column_justification')}</th>
              <th className="w-16 px-3 py-2 text-center font-medium" />
              <th className="w-16 px-3 py-2 text-left font-medium" />
            </tr>
          </thead>
          <tbody>
            {run.results.map((r, i) => (
              <ResultRow key={r.id} result={r} index={i} scenario={scenario} runId={runId} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Progress bar — placed below the table so the table doesn't jump up
          when the bar disappears once the run finishes. */}
      {(run.status === 'running' || run.status === 'pending') && (
        <div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {t('messages_processed', {
                completed: stats.completed,
                total: run.total_messages,
              })}
            </span>
            <span>{stats.progress}%</span>
          </div>
          <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-info transition-all duration-700 ease-out"
              style={{ width: `${stats.progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const ResultRow = ({
  result: r,
  index,
  scenario,
  runId,
}: {
  result: RunResultDto;
  index: number;
  scenario: string;
  runId: number;
}) => {
  const { t } = useTranslation('simulation_page');
  const queryClient = useQueryClient();

  const onFeedbackChanged = () => {
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.simulation.runDetail, runId.toString()],
    });
  };

  return (
    <tr className="border-b last:border-0">
      <td className="px-3 py-2 text-muted-foreground">{index + 1}</td>

      {/* Message */}
      <td className="max-w-[200px] px-3 py-2 align-top">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="block truncate">{r.user_message}</span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm">
              <p className="whitespace-pre-wrap text-xs">{r.user_message}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </td>

      {/* A result settled out of EVALUATING keeps the response it produced, so the failure shows next to it. */}
      <td className="max-w-[300px] px-3 py-2">
        {r.error_message || r.ai_response ? (
          <div className="space-y-1">
            {r.error_message && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      data-testid={`result-error-${toResultErrorCode(r.error_message)}`}
                      className="block truncate text-xs text-destructive"
                    >
                      {resultErrorText(t, r.error_message)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-lg">
                    <p className="whitespace-pre-wrap text-xs">
                      {resultErrorText(t, r.error_message)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {r.ai_response && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="block truncate text-muted-foreground">{r.ai_response}</span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-lg">
                    <p className="whitespace-pre-wrap text-xs">{r.ai_response}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </td>

      {/* AI Score */}
      <td className="px-3 py-2">
        {r.eval_score != null ? (
          <span className={`font-semibold ${getScoreColor(r.eval_score)}`}>{r.eval_score}/5</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </td>

      {/* Justification */}
      <td className="max-w-[240px] px-3 py-2">
        {r.eval_justification ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block truncate text-xs text-muted-foreground">
                  {r.eval_justification}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-lg">
                <p className="whitespace-pre-wrap text-xs">{r.eval_justification}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </td>

      {/* Thumbs up/down */}
      <td className="px-3 py-2 text-center">
        {r.ai_message_id && r.conversation_conv_id ? (
          <TooltipProvider>
            <div className="inline-flex" data-testid={`feedback-buttons-${r.id}`}>
              <MessageFeedbackButton
                conversationId={r.conversation_conv_id}
                messageId={r.ai_message_id.toString()}
                existingFeedback={r.feedback}
                nextStepsContext="simulation"
                onFeedbackChanged={onFeedbackChanged}
              />
            </div>
          </TooltipProvider>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </td>

      {/* View link */}
      <td className="px-3 py-2">
        {r.conversation_conv_id && (
          <a
            href={`/i/${scenario}/conversation/${r.conversation_conv_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-info hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            {t('view_conversation')}
          </a>
        )}
      </td>
    </tr>
  );
};
